"use client";
import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';

const slides = [
    {
        id: 1,
        title: "Autonet DMS",
        subtitle: "Enterprise Dealership Management",
        description: "The complete ecosystem for large-scale automobile dealerships. Robust, reliable, and industry-proven.",
        image: "/images/unsplash/1497215728101-856f4ea42174-1200.jpg",
        cta: "Explore Autonet",
        link: "/products/autonet-dms"
    },
    {
        id: 2,
        title: "AutoNext Cloud",
        subtitle: "The Future of DMS",
        description: "Experience the freedom of cloud-based management. Access your dealership data from anywhere, anytime.",
        image: "/images/unsplash/1553877522-43269d4ea984-1200.jpg",
        cta: "Try AutoNext",
        link: "/products/autonext-dms"
    },
    {
        id: 3,
        title: "MyPaper",
        subtitle: "Document Management",
        description: "MyPaper is an software platform tailored to the automotive industry that seamlessly integrates with your existing systems.",
        image: "/images/unsplash/1552664730-d307ca884978-1200.jpg",
        cta: "Try MyPaper",
        link: "/products/mypaper"
    },
];

const Hero = () => {
    const [current, setCurrent] = useState(0);

    // Auto-slide every 5 seconds
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrent((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    const nextSlide = () => setCurrent(current === slides.length - 1 ? 0 : current + 1);
    const prevSlide = () => setCurrent(current === 0 ? slides.length - 1 : current - 1);

    return (
        <section className="relative h-screen w-full pt-20 overflow-hidden bg-slate-900">
            {/* Slides Wrapper */}
            <div className="relative h-full w-full ">
                {slides.map((slide, index) => (
                    <div
                        key={slide.id}
                        className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${index === current ? "opacity-100 z-10" : "opacity-0 z-0"
                            }`}
                    >
                        {/* Background Image with Overlay */}
                        <Image
                            src={slide.image}
                            alt={slide.title}
                            fill
                            className="object-cover"
                            priority={index === 0}
                            unoptimized
                        />
                        {/* Improved Overlay: Darker on mobile for text readability */}
                        <div className="absolute inset-0 bg-slate-900/60 md:bg-transparent md:bg-gradient-to-r md:from-slate-900 md:via-slate-900/60 md:to-transparent" />

                        {/* Content Container */}
                        <div className="container mx-auto px-6 sm:px-12 md:px-24 h-full flex items-center relative z-20">
                            <div className={`max-w-2xl space-y-4 md:space-y-6 transition-all duration-700 delay-300 ${index === current ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
                                }`}>
                                <div className="space-y-2">
                                    <p className="text-brand font-bold tracking-[0.15em] md:tracking-[0.2em] text-xs md:text-sm">
                                        {slide.subtitle}
                                    </p>
                                    {index === 0 ? (
                                        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-black text-white leading-tight">
                                            {slide.title}
                                        </h1>
                                    ) : (
                                        <p className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-black text-white leading-tight">
                                            {slide.title}
                                        </p>
                                    )}
                                </div>
                                <p className="text-base md:text-lg text-slate-200 leading-relaxed max-w-lg">
                                    {slide.description}
                                </p>
                                <div className="pt-4">
                                    <Link
                                        href={slide.link}
                                        prefetch={false}
                                        className="bg-brand text-white px-8 md:px-10 py-3 md:py-4 rounded-xl font-bold flex items-center justify-center w-full sm:w-fit gap-3 hover:scale-105 transition-all shadow-xl shadow-brand/20"
                                    >
                                        {slide.cta}
                                        <ArrowRight size={20} />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Navigation Arrows - Responsive Placement */}
            <div className="absolute bottom-20 left-auto right-20 md:right-auto md:left-6 md:inset-y-0 md:flex md:items-center z-30">
                <button
                    onClick={prevSlide}
                    aria-label="Previous slide"
                    className="p-3 rounded-full bg-white/10 backdrop-blur-md text-white hover:bg-brand transition-all border border-white/20"
                >
                    <ChevronLeft size={20} className="md:w-6 md:h-6" />
                </button>
            </div>

            <div className="absolute bottom-20 right-6 md:inset-y-0 md:flex md:items-center z-30">
                <button
                    onClick={nextSlide}
                    aria-label="Next slide"
                    className="p-3 rounded-full bg-white/10 backdrop-blur-md text-white hover:bg-brand transition-all border border-white/20"
                >
                    <ChevronRight size={20} className="md:w-6 md:h-6" />
                </button>
            </div>

            {/* Pagination Dots */}
            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-30 flex gap-3">
                {slides.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => setCurrent(index)}
                        aria-label={`Go to slide ${index + 1}`}
                        className={`h-1.5 transition-all duration-300 rounded-full ${index === current ? "w-10 bg-brand" : "w-4 bg-white/40"
                            }`}
                    />
                ))}
            </div>
        </section>
    );
};

export default Hero;